import * as React from "react";
import { cn } from "../../lib/utils";

export type SegmentOption = {
  label: string;
  value: string;
  icon?: React.ReactNode;
};

export interface SegmentToggleProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "onChange"
> {
  value?: string;
  onChange?: (value: string) => void;
  options?: SegmentOption[];
  segmentClassName?: string;
}

export const DEFAULT_SEGMENT_OPTIONS: SegmentOption[] = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const activeSegmentStyle: React.CSSProperties = {
  borderRadius: "12px",
  borderTop: "0px solid var(--color-primary)",
  borderRight: "0.5px solid var(--color-primary)",
  borderBottom: "0.9px solid var(--color-primary)",
  borderLeft: "0.5px solid var(--color-primary)",
  background: "var(--color-primary-gradient)",
  boxShadow: "0 1px 1px rgba(5, 25, 18, 0.25)",
  color: "var(--color-white)",
};

const SegmentToggle = React.forwardRef<HTMLDivElement, SegmentToggleProps>(
  (
    { value, onChange, options = DEFAULT_SEGMENT_OPTIONS, className, segmentClassName, ...props },
    ref
  ) => {
    const resolvedValue = value ?? options[0]?.value;

    return (
      <div
        ref={ref}
        className={cn(
          "inline-flex rounded-[14px] p-[2px] items-center bg-white",
          className
        )}
        {...props}
      >
        {options?.map((option) => {
          const isActive = option.value === resolvedValue;

          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={isActive}
              onClick={() => {
                if (!isActive) {
                  onChange?.(option.value);
                }
              }}
              style={
                isActive
                  ? activeSegmentStyle
                  : {
                      color: "var(--color-black-8)",
                    }
              }
              className={cn(
                "flex h-8 w-[65px] md:w-[80px] items-center justify-center gap-2 rounded-[12px] px-2 md:px-3 text-[12px] md:text-[14px] tracking-[-0.56px] transition-colors",
                isActive ? "font-medium" : "font-normal",
                segmentClassName
              )}
            >
              {option.icon && (
                <span className="inline-flex items-center justify-center shrink-0">
                  {option.icon}
                </span>
              )}
              <span>{option.label}</span>
            </button>
          );
        })}
      </div>
    );
  }
);

SegmentToggle.displayName = "SegmentToggle";

export { SegmentToggle };
